import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  X, 
  RotateCcw, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Infer question type from question data
 * SINGLE SOURCE OF TRUTH - matches ChallengePage.jsx logic
 */
function inferQuestionType(question) {
  if (!question) return 'multiple_choice';
  
  // Explicit type takes priority
  if (question.question_type) {
    if (['multi_select', 'fill_blank', 'matching'].includes(question.question_type)) {
      return question.question_type;
    }
  }
  
  // Infer from structure
  if (question.pairs || question.matching_pairs) return 'matching';
  if (question.correct_options && Array.isArray(question.correct_options)) return 'multi_select';
  if (question.correct_answer && !question.options) return 'fill_blank';
  if (question.question_text?.includes('___')) return 'fill_blank';
  
  return 'multiple_choice';
}

/**
 * Get display name for question type
 */
function getTypeBadge(type) {
  const badges = {
    'multiple_choice': { label: 'Multiple Choice', color: 'bg-blue-100 text-blue-800' },
    'multi_select': { label: 'Multi-Select', color: 'bg-purple-100 text-purple-800' },
    'fill_blank': { label: 'Fill in Blank', color: 'bg-amber-100 text-amber-800' },
    'matching': { label: 'Matching', color: 'bg-green-100 text-green-800' },
    'numeric_evaluation': { label: 'Multiple Choice', color: 'bg-blue-100 text-blue-800' },
    'solve_equation': { label: 'Multiple Choice', color: 'bg-blue-100 text-blue-800' }
  };
  return badges[type] || badges['multiple_choice'];
}

/**
 * Editor for Multiple Choice questions
 */
function MultipleChoiceEditor({ question, onChange, errors }) {
  const options = question.options || ['', '', '', ''];
  const correctOption = question.correct_option ?? 0;
  
  const updateOption = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ ...question, options: newOptions });
  };
  
  const setCorrect = (index) => {
    onChange({ ...question, correct_option: index });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Question Text</Label>
        <Textarea
          value={question.question_text || ''}
          onChange={(e) => onChange({ ...question, question_text: e.target.value })}
          placeholder="Enter the question..."
          className="mt-1"
          rows={3}
        />
        {errors?.question_text && (
          <p className="text-sm text-red-500 mt-1">{errors.question_text}</p>
        )}
      </div>
      
      <div>
        <Label>Answer Options (select the correct one)</Label>
        <div className="space-y-2 mt-2">
          {options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCorrect(idx)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  correctOption === idx 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {String.fromCharCode(65 + idx)}
              </button>
              <Input
                value={opt}
                onChange={(e) => updateOption(idx, e.target.value)}
                placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                className="flex-1"
              />
              {correctOption === idx && (
                <CheckCircle className="w-5 h-5 text-green-500" />
              )}
            </div>
          ))}
        </div>
        {errors?.options && (
          <p className="text-sm text-red-500 mt-1">{errors.options}</p>
        )}
      </div>
      
      <div>
        <Label>Explanation (shown after answering)</Label>
        <Textarea
          value={question.explanation || ''}
          onChange={(e) => onChange({ ...question, explanation: e.target.value })}
          placeholder="Explain the correct answer..."
          className="mt-1"
          rows={2}
        />
      </div>
    </div>
  );
}

/**
 * Editor for Multi-Select questions
 */
function MultiSelectEditor({ question, onChange, errors }) {
  const options = question.options || ['', '', '', '', ''];
  const correctOptions = question.correct_options || [];
  
  const updateOption = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ ...question, options: newOptions });
  };
  
  const toggleCorrect = (index) => {
    const newCorrect = correctOptions.includes(index)
      ? correctOptions.filter(i => i !== index)
      : [...correctOptions, index].sort((a, b) => a - b);
    onChange({ ...question, correct_options: newCorrect });
  };
  
  const addOption = () => {
    onChange({ ...question, options: [...options, ''] });
  };
  
  const removeOption = (index) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, i) => i !== index);
    const newCorrect = correctOptions
      .filter(i => i !== index)
      .map(i => i > index ? i - 1 : i);
    onChange({ ...question, options: newOptions, correct_options: newCorrect });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Question Text</Label>
        <Textarea
          value={question.question_text || ''}
          onChange={(e) => onChange({ ...question, question_text: e.target.value })}
          placeholder="Select all that apply: ..."
          className="mt-1"
          rows={3}
        />
      </div>
      
      <div>
        <Label>Answer Options (select ALL correct answers)</Label>
        <div className="space-y-2 mt-2">
          {options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleCorrect(idx)}
                className={`w-8 h-8 rounded flex items-center justify-center text-sm font-medium transition-colors ${
                  correctOptions.includes(idx)
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {correctOptions.includes(idx) ? '✓' : String.fromCharCode(65 + idx)}
              </button>
              <Input
                value={opt}
                onChange={(e) => updateOption(idx, e.target.value)}
                placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                className="flex-1"
              />
              {options.length > 2 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOption(idx)}
                >
                  <Trash2 className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addOption}
          className="mt-2"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Option
        </Button>
        {errors?.correct_options && (
          <p className="text-sm text-red-500 mt-1">{errors.correct_options}</p>
        )}
      </div>
      
      <div>
        <Label>Explanation</Label>
        <Textarea
          value={question.explanation || ''}
          onChange={(e) => onChange({ ...question, explanation: e.target.value })}
          placeholder="Explain the correct answers..."
          className="mt-1"
          rows={2}
        />
      </div>
    </div>
  );
}

/**
 * Editor for Fill-in-the-Blank questions
 */
function FillBlankEditor({ question, onChange, errors }) {
  const acceptedAnswers = question.accepted_answers || 
    (question.correct_answer ? [question.correct_answer] : []);
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Question Text (use ___ for the blank)</Label>
        <Textarea
          value={question.question_text || ''}
          onChange={(e) => onChange({ ...question, question_text: e.target.value })}
          placeholder="The capital of France is ___."
          className="mt-1"
          rows={3}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Use three underscores (___) to mark where the blank should appear.
        </p>
      </div>
      
      <div>
        <Label>Primary Correct Answer</Label>
        <Input
          value={question.correct_answer || ''}
          onChange={(e) => onChange({ ...question, correct_answer: e.target.value })}
          placeholder="Paris"
          className="mt-1"
        />
        {errors?.correct_answer && (
          <p className="text-sm text-red-500 mt-1">{errors.correct_answer}</p>
        )}
      </div>
      
      <div>
        <Label>Additional Accepted Answers (comma-separated)</Label>
        <Input
          value={acceptedAnswers.join(', ')}
          onChange={(e) => {
            const answers = e.target.value.split(',').map(a => a.trim()).filter(Boolean);
            onChange({ ...question, accepted_answers: answers });
          }}
          placeholder="paris, París"
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Fuzzy matching (~85% similarity) is applied automatically for minor typos.
        </p>
      </div>
      
      <div>
        <Label>Explanation</Label>
        <Textarea
          value={question.explanation || ''}
          onChange={(e) => onChange({ ...question, explanation: e.target.value })}
          placeholder="Explain the answer..."
          className="mt-1"
          rows={2}
        />
      </div>
    </div>
  );
}

/**
 * Editor for Matching questions
 */
function MatchingEditor({ question, onChange, errors }) {
  const pairs = question.pairs || question.matching_pairs || [
    { left: '', right: '' },
    { left: '', right: '' }
  ];
  
  const updatePair = (index, side, value) => {
    const newPairs = pairs.map((p, i) => 
      i === index ? { ...p, [side]: value } : p
    );
    onChange({ ...question, pairs: newPairs });
  };
  
  const addPair = () => {
    onChange({ ...question, pairs: [...pairs, { left: '', right: '' }] });
  };
  
  const removePair = (index) => {
    if (pairs.length <= 2) return;
    onChange({ ...question, pairs: pairs.filter((_, i) => i !== index) });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Question Text (instructions)</Label>
        <Textarea
          value={question.question_text || ''}
          onChange={(e) => onChange({ ...question, question_text: e.target.value })}
          placeholder="Match each term with its definition..."
          className="mt-1"
          rows={2}
        />
      </div>
      
      <div>
        <Label>Matching Pairs</Label>
        <div className="space-y-2 mt-2">
          {pairs.map((pair, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="w-6 text-center text-sm text-muted-foreground">{idx + 1}</span>
              <Input
                value={pair.left || ''}
                onChange={(e) => updatePair(idx, 'left', e.target.value)}
                placeholder="Term"
                className="flex-1"
              />
              <span className="text-muted-foreground">→</span>
              <Input
                value={pair.right || ''}
                onChange={(e) => updatePair(idx, 'right', e.target.value)}
                placeholder="Definition"
                className="flex-1"
              />
              {pairs.length > 2 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePair(idx)}
                >
                  <Trash2 className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addPair}
          className="mt-2"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Pair
        </Button>
        {errors?.pairs && (
          <p className="text-sm text-red-500 mt-1">{errors.pairs}</p>
        )}
      </div>
      
      <div>
        <Label>Explanation</Label>
        <Textarea
          value={question.explanation || ''}
          onChange={(e) => onChange({ ...question, explanation: e.target.value })}
          placeholder="Explain the correct matches..."
          className="mt-1"
          rows={2}
        />
      </div>
    </div>
  );
}

/**
 * Main Question Editor Component
 */
export default function QuestionEditor({ 
  challengeId, 
  onClose, 
  onSaved 
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editedQuestion, setEditedQuestion] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [challengeInfo, setChallengeInfo] = useState(null);
  
  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, [challengeId]);
  
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/challenges/${challengeId}/questions`);
      
      if (res.data.success) {
        setQuestions(res.data.questions);
        setChallengeInfo({
          subject: res.data.subject,
          grade_level: res.data.grade_level
        });
        if (res.data.questions.length > 0) {
          setEditedQuestion({ ...res.data.questions[0] });
        }
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to edit this challenge');
        onClose();
      } else {
        toast.error('Failed to load questions');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const currentQuestion = questions[currentIndex];
  const questionType = currentQuestion ? inferQuestionType(currentQuestion) : 'multiple_choice';
  const typeBadge = getTypeBadge(questionType);
  
  // Navigate to question
  const goToQuestion = (index) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setCurrentIndex(index);
    setEditedQuestion({ ...questions[index] });
    setHasChanges(false);
    setErrors({});
  };
  
  // Handle question changes
  const handleQuestionChange = (updated) => {
    setEditedQuestion(updated);
    setHasChanges(true);
    setErrors({});
  };
  
  // Validate before save
  const validate = () => {
    const errs = {};
    const q = editedQuestion;
    const type = inferQuestionType(q);
    
    if (!q.question_text?.trim()) {
      errs.question_text = 'Question text is required';
    }
    
    if (type === 'multiple_choice' || type === 'numeric_evaluation' || type === 'solve_equation') {
      if (!q.options || q.options.filter(o => o?.trim()).length < 2) {
        errs.options = 'At least 2 options required';
      }
      if (q.correct_option === undefined || q.correct_option === null) {
        errs.options = 'Select a correct answer';
      }
    }
    
    if (type === 'multi_select') {
      if (!q.correct_options || q.correct_options.length < 1) {
        errs.correct_options = 'Select at least 1 correct answer';
      }
    }
    
    if (type === 'fill_blank') {
      if (!q.correct_answer?.trim()) {
        errs.correct_answer = 'Correct answer is required';
      }
    }
    
    if (type === 'matching') {
      const pairs = q.pairs || q.matching_pairs || [];
      if (pairs.length < 2) {
        errs.pairs = 'At least 2 pairs required';
      }
      const hasEmpty = pairs.some(p => !p.left?.trim() || !p.right?.trim());
      if (hasEmpty) {
        errs.pairs = 'All pairs must have both left and right values';
      }
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  // Save changes
  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        question_text: editedQuestion.question_text,
        explanation: editedQuestion.explanation
      };
      
      const type = inferQuestionType(editedQuestion);
      
      if (type === 'multiple_choice' || type === 'numeric_evaluation' || type === 'solve_equation') {
        payload.options = editedQuestion.options;
        payload.correct_option = editedQuestion.correct_option;
      } else if (type === 'multi_select') {
        payload.options = editedQuestion.options;
        payload.correct_options = editedQuestion.correct_options;
      } else if (type === 'fill_blank') {
        payload.correct_answer = editedQuestion.correct_answer;
        payload.accepted_answers = editedQuestion.accepted_answers;
      } else if (type === 'matching') {
        payload.pairs = editedQuestion.pairs || editedQuestion.matching_pairs;
      }
      
      await axios.patch(
        `${API}/challenges/${challengeId}/questions/${currentIndex}`,
        payload
      );
      
      // Update local state
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = { ...editedQuestion, has_overrides: true };
      setQuestions(updatedQuestions);
      setHasChanges(false);
      
      toast.success('Question saved');
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving question:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Revert to original
  const handleRevert = async () => {
    if (!window.confirm('Revert this question to its original state?')) {
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(
        `${API}/challenges/${challengeId}/questions/${currentIndex}/revert`,
        {}
      );
      
      // Refresh questions
      await fetchQuestions();
      toast.success('Question reverted to original');
    } catch (err) {
      console.error('Error reverting:', err);
      toast.error('Failed to revert');
    } finally {
      setSaving(false);
    }
  };
  
  // Cancel/discard changes
  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm('Discard unsaved changes?')) {
        return;
      }
    }
    setEditedQuestion({ ...questions[currentIndex] });
    setHasChanges(false);
    setErrors({});
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-3xl mx-4">
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-sage-500" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <Card className="w-full max-w-3xl mx-4 my-auto overflow-visible">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Edit Questions</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {challengeInfo?.subject} • {challengeInfo?.grade_level}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Creator Mode Banner */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Creator Mode</strong> — Edits affect this challenge for all students.
            </p>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 overflow-visible">
          {/* Question Navigator */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToQuestion(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Question {currentIndex + 1} of {questions.length}
              </span>
              <Badge className={typeBadge.color}>{typeBadge.label}</Badge>
              {currentQuestion?.has_overrides && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  Edited
                </Badge>
              )}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToQuestion(Math.min(questions.length - 1, currentIndex + 1))}
              disabled={currentIndex === questions.length - 1}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {/* Question Editor */}
          {editedQuestion && (
            <div className="space-y-4">
              {(questionType === 'multiple_choice' || questionType === 'numeric_evaluation' || questionType === 'solve_equation') && (
                <MultipleChoiceEditor
                  question={editedQuestion}
                  onChange={handleQuestionChange}
                  errors={errors}
                />
              )}
              
              {questionType === 'multi_select' && (
                <MultiSelectEditor
                  question={editedQuestion}
                  onChange={handleQuestionChange}
                  errors={errors}
                />
              )}
              
              {questionType === 'fill_blank' && (
                <FillBlankEditor
                  question={editedQuestion}
                  onChange={handleQuestionChange}
                  errors={errors}
                />
              )}
              
              {questionType === 'matching' && (
                <MatchingEditor
                  question={editedQuestion}
                  onChange={handleQuestionChange}
                  errors={errors}
                />
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRevert}
              disabled={saving || !currentQuestion?.has_overrides}
              className="text-muted-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Revert to Original
            </Button>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving || !hasChanges}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={
                  saving || !hasChanges 
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed hover:bg-gray-200" 
                    : "bg-sage-500 hover:bg-sage-900 text-white cursor-pointer"
                }
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
